import { component$ } from "@builder.io/qwik";
import { Card, CardContent } from "~/components/ui/card";
import { ProfileSectionHeader } from "~/components/ui/profile-section-header";
import { MarkdownContent } from "~/components/markdown-content";
import { OfferCard } from "~/components/offer-card";
import { CoachResultsCard } from "~/components/coach-results-card";
import { CertificateItem } from "~/components/ui/certificate-item";
import { Testimonials } from "~/components/ui/testimonials";
import { type DocumentHead, Link } from "@builder.io/qwik-city";
import { Button } from "~/components/ui/button";
import { ResourceCard } from "~/components/ui/resource-card";
import { hasFeature, Feature } from "~/lib/has-feature";
import { useCoachData } from "../layout";
import { ImageSparkle } from "~/components/ui/image-sparkle";
import { CDNImage } from "~/components/ui/cdn-image";
import { Badge } from "~/components/ui/badge";
import { VideoPlayer } from "~/components/ui/video-player";

import type { CoachData } from "~/shared/loaders";
import { MembershipPlan } from "~/lib/schemas/index";

export const head: DocumentHead = ({ resolveValue }) => {
  const coach = resolveValue(useCoachData);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": `https://dreamcoachmatch.com/${coach.username}#webpage`,
    url: `https://dreamcoachmatch.com/${coach.username}`,
    name: `${coach.fullName} - Dream Coach Match`,
    description: coach.profile?.description,
    mainEntity: {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": `https://dreamcoachmatch.com/${coach.username}#person`,
      name: coach.fullName,
      givenName: coach.firstName,
      familyName: coach.lastName,
      alternateName: coach.username,
      description: coach.profile?.description,
      image: coach.profile?.mainImage,
      jobTitle: coach.profile?.title,
      url: `https://dreamcoachmatch.com/${coach.username}`,
      ...(coach.certifications.length > 0 && {
        hasCredential: coach.certifications.map((cert) => ({
          "@type": "EducationalOccupationalCredential",
          name: cert.title,
        })),
      }),
    },
  };

  return {
    meta: [
      {
        property: "og:type",
        content: "profile",
      },
      {
        property: "og:image",
        content: `https://dreamcoachmatch.com/${coach.username}/social-image.svg`,
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:image",
        content: `https://dreamcoachmatch.com/${coach.username}/social-image.svg`,
      },
      ...(coach.firstName
        ? [
            {
              property: "og:profile:first_name",
              content: coach.firstName,
            },
          ]
        : []),
      ...(coach.lastName
        ? [
            {
              property: "og:profile:last_name",
              content: coach.lastName,
            },
          ]
        : []),
      {
        property: "og:profile:username",
        content: coach.username,
      },
    ],
    scripts: [
      {
        key: "jsonld-person",
        props: {
          type: "application/ld+json",
          dangerouslySetInnerHTML: JSON.stringify(jsonLd),
        },
      },
    ],
  };
};

import { useOtherOffers } from "~/shared/loaders";
import { CoachLeadMagnet } from "~/components/coach-lead-magnet";
export { useOtherOffers } from "~/shared/loaders";

export default component$(() => {
  const data = useCoachData();
  const otherOffers = useOtherOffers();
  const coach = data.value;
  if (!coach.profile) {
    return "Coach not found.";
  }

  return (
    <>
      <MainProfile coach={coach} otherOffers={otherOffers.value} />
    </>
  );
});

export const MainProfile = component$<{
  coach: CoachData;
  otherOffers: {
    id: string;
    headline: string | null;
    description: string | null;
    tags: { id: string; title: string }[];
  }[];
}>(({ coach, otherOffers }) => {
  return (
    <>
      <div class="flex-basis-0 flex-grow-6 flex flex-col items-stretch gap-8">
        {(coach.profile.title || coach.profile.description) && (
          <div>
            <ProfileSectionHeader text="About Me" />
            <Card>
              <CardContent>
                <div class="flex gap-4 md:gap-8">
                  {hasFeature(coach, Feature.FancyProfile) && (
                    <>
                      {coach.profile.mainVideo ? (
                        <div class="flex-basis-0 flex-grow-2 relative">
                          <VideoPlayer
                            src={coach.profile.mainVideo}
                            title={coach.fullName}
                            class="aspect-[2/3] w-full rounded-lg"
                          />
                          <ImageSparkle />
                        </div>
                      ) : (
                        coach.profile.mainImage && (
                          <div class="flex-basis-0 flex-grow-2 relative">
                            <CDNImage
                              src={coach.profile.mainImage}
                              alt={coach.fullName}
                              width={256}
                              height={384}
                              class="block aspect-[2/3] w-full rounded-lg object-cover"
                            />
                            <ImageSparkle />
                          </div>
                        )
                      )}
                    </>
                  )}
                  {(coach.profile.title || coach.profile.description) && (
                    <div class="flex-basis-0 flex-grow-4 flex w-full flex-col justify-start">
                      {coach.profile.title && (
                        <h2 class="text-primary font-heading text-3xl">
                          {coach.profile.title}
                        </h2>
                      )}
                      {coach.profile.description && (
                        <div class="text-secondary mt-4 border-l-2 border-l-orange-200 py-1 pl-2">
                          <MarkdownContent
                            content={coach.profile.description}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {coach.profile.leadMagnetTitle && coach.profile.leadMagnetUrl && (
          <CoachLeadMagnet
            leadMagnetTitle={coach.profile.leadMagnetTitle}
            leadMagnetDescription={coach.profile.leadMagnetDescription}
            leadMagnetCtaText={coach.profile.leadMagnetCtaText}
          />
        )}

        {coach.mainOffer?.headline && coach.mainOffer.description && (
          <div>
            <ProfileSectionHeader text="My Main Coaching Offer" />
            <OfferCard
              offer={coach.mainOffer}
              coach={coach}
              ctaButtonText={coach.profile.ctaButtonText!}
            />
          </div>
        )}

        {coach.membershipPlan === MembershipPlan.ProPlus &&
          otherOffers.length > 0 && (
            <div>
              <ProfileSectionHeader text="Other Packages" />
              <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                {otherOffers.map((offer) => (
                  <Card key={offer.id}>
                    <CardContent class="flex h-full flex-col gap-4">
                      <div class="flex items-start gap-2">
                        <div>
                          {offer.tags.length > 0 && (
                            <div class="flex items-center gap-2">
                              {offer.tags.map((t) => (
                                <Badge key={t.id} variant="outline-secondary">
                                  {t.title}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <h3 class="font-heading mt-2 text-xl">
                            {offer.headline}
                          </h3>
                          <p class="text-secondary mt-2 line-clamp-3 text-sm">
                            {offer.description}
                          </p>
                        </div>
                      </div>
                      <div class="mt-auto">
                        <Link href={`/${coach.username}/offer/${offer.id}`}>
                          <Button variant="outline" class="w-full">
                            Details
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

        {(coach.profile.whyContent || coach.profile.whyTitle) && (
          <div>
            <ProfileSectionHeader text="Why Work With Me" />
            <Card>
              <CardContent class="space-y-4">
                {coach.profile.whyTitle && (
                  <h2 class="font-heading text-2xl">
                    {coach.profile.whyTitle}
                  </h2>
                )}

                {coach.profile.whyContent && (
                  <div class="text-secondary mt-4 border-l-2 border-l-orange-200 py-1 pl-2">
                    <MarkdownContent content={coach.profile.whyContent} />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {coach.results.length > 0 && (
          <div>
            <ProfileSectionHeader text="Results I help my clients achieve" />
            <CoachResultsCard results={coach.results} />
          </div>
        )}

        {coach.certifications.length > 0 && (
          <div>
            <ProfileSectionHeader text="Certificates" />
            <Card>
              <CardContent>
                <div class="grid grid-cols-1 gap-4">
                  {coach.certifications.map((cert) => (
                    <CertificateItem key={cert.id} {...cert} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {coach.testimonials?.length > 0 && (
          <div>
            <ProfileSectionHeader text="Testimonials" />
            <Testimonials testimonials={coach.testimonials} />
          </div>
        )}

        {hasFeature(coach, Feature.Resources) &&
          coach.resources?.length > 0 && (
            <div>
              <ProfileSectionHeader text="Resources" />
              <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                {coach.resources.slice(0, 2).map((resource) => (
                  <ResourceCard
                    class="h-full"
                    key={resource.id}
                    resource={resource}
                    coachUsername={coach.username}
                  />
                ))}
              </div>
            </div>
          )}

        {coach.ghlCalendarId && (
          <Link href={`/${coach.username}/book`} class="mx-auto mb-16">
            <Button variant="fancy" size="lg">
              {coach.profile.ctaButtonText}
            </Button>
          </Link>
        )}
      </div>
    </>
  );
});
